/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { CSSProperties, FC, forwardRef } from "react";
import ReactSelect, { GetOptionLabel, GetOptionValue, MultiValue, SingleValue } from "react-select";
import SelectType from "react-select/dist/declarations/src/Select";
import Feedback from "../feedback";
import { IFeedback } from "../types";

export interface IOption {
    value: string;
    label: string;
    data?: any;
    readonly color?: string;
    readonly isFixed?: boolean;
    readonly isDisabled?: boolean;
}

export interface GroupedOption {
    readonly label: string;
    readonly options: readonly IOption[];
}

export interface ISelectProps extends IFeedback {
    options: IOption[] | GroupedOption[];
    defaultValue?: IOption;
    value?: IOption | null;
    name?: string;
    onChange?: (newValue: MultiValue<IOption> | SingleValue<IOption> | any) => void;
    readonly isClearable?: boolean;
    readonly isDisabled?: boolean;
    readonly isLoading?: boolean;
    readonly isRtl?: boolean;
    readonly isSearchable?: boolean;
    readonly isMulti?: boolean;
    placeholder?: string;
    feedbackText?: string;
    showBorder?: boolean;
    bgColor?: string;
    color?: string;
    fontSize?: number;
    getOptionLabel?: GetOptionLabel<IOption>;
    getOptionValue?: GetOptionValue<IOption>;
}

const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};
const groupBadgeStyles: CSSProperties = {
    backgroundColor: "#EBECF0",
    borderRadius: "2em",
    color: "#172B4D",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
};

const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
        <span>{data.label}</span>
        <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
);

const DropdownIndicator = () => {
    return (
        <span className="w-8 h-full inline-flex justify-center items-center">
            <i className="fa fa-caret-down" />
        </span>
    );
};

const Select = forwardRef<SelectType<IOption, boolean, GroupedOption>, ISelectProps>((
    {
        options,
        defaultValue,
        value,
        isClearable,
        isDisabled,
        isLoading,
        isRtl,
        isSearchable,
        isMulti,
        placeholder,
        onChange,
        showState,
        showErrorOnly,
        state,
        feedbackText,
        showBorder = true,
        bgColor = "white",
        color,
        fontSize = 14,
        getOptionLabel,
        getOptionValue
    }, ref
) => {
    const customStyles = {
        control: (provided: any, inputState: any) => ({
            ...provided,
            fontSize,
            backgroundColor: bgColor,
            borderRadius: 6,
            borderColor: "#e5e7eb",
            border: showBorder ? "" : "none",
            minHeight: 32,
            boxShadow: "none",
            color: color ? color : "",
            marginTop: 0,
            "&:hover": {
                borderColor: inputState.isFocused && "#e6e7eb",
            },
        }),
        menu: (provided: any) => ({
            ...provided,
            borderRadius: 6,
            padding: 4,
            marginTop: -2,
            boxShadow: "none",
            border: "1px solid #e6e6e6",
            zIndex: 20,
            minWidth: "100px"
        }),
        option: (provided: any, inputState: any) => ({
            ...provided,
            color:
                inputState.isFocused && inputState.isSelected
                    ? "#374151"
                    : inputState.isSelected
                        ? "#868ba1"
                        : inputState.isFocused
                            ? "#374151"
                            : "#868ba1",
            borderRadius: 6,
            fontSize: 12,
            backgroundColor:
                inputState.isFocused && inputState.isSelected
                    ? "#e6e7eb"
                    : inputState.isSelected
                        ? "transparent"
                        : inputState.isFocused
                            ? "#e6e7eb"
                            : inputState.isDisabled
                                ? "#d8d9d9"
                                : "transparent",
            cursor: inputState.isDisabled ? "not-allowed" : "pointer",
            "&:hover": {
                backgroundColor: !inputState.isDisabled && "#e6e7eb",
                color: !inputState.isDisabled && "#374151",
            },
        }),
        multiValue: (provided: any, inputState: any) => ({
            ...provided,
            backgroundColor: inputState.isDisabled ? "#adb5bd" : "#e6e7eb",
            borderRadius: 0,
            flexDirection: "row-reverse",
        }),
        multiValueLabel: (provided: any, inputState: any) => ({
            ...provided,
            color: "#374151",
            paddingRight: 10,
            paddingLeft: inputState?.isDisabled ? 10 : 0,
            paddingTop: 5,
        }),
        multiValueRemove: (provided: any, inputState: any) => ({
            ...provided,
            color: "#374151",
            opacity: 0.5,
            padding: 2,
            display: inputState.isDisabled ? "none" : "inline",
            "&:hover": {
                backgroundColor: "transparent",
                color: "#374151",
                opacity: 1,
            },
        }),
        singleValue: (provided: any, inputState: any) => ({
            ...provided,
            color: color ? color : "374151",
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        indicatorsContainer: () => ({
            marginLeft: "-13px",
        }),

    };
    return (
        <>
            <ReactSelect
                styles={customStyles}
                options={options}
                defaultValue={defaultValue}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isRtl={isRtl}
                isSearchable={isSearchable}
                isMulti={isMulti}
                formatGroupLabel={formatGroupLabel}
                placeholder={placeholder}
                components={{ DropdownIndicator }}
                value={value}
                onChange={onChange}
                ref={ref}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
            />
            {feedbackText && showState && (
                <Feedback state={state} showErrorOnly={showErrorOnly}>
                    {feedbackText}
                </Feedback>
            )}
        </>
    );
});

Select.displayName = "Select";

export default Select;
